// LearnCore Frontend - 学习事件追踪器

import { progressApi } from '../api/tasks';

export interface LearningEvent {
  event_type: 'study_session' | 'card_completed' | 'quiz_completed' | 'passage_read';
  event_data?: any;
  study_time_seconds?: number;
  accuracy_rate?: number;
  cards_count?: number;
  passage_id?: string;
  set_id?: string;
}

export class LearningEventTracker {
  private static instance: LearningEventTracker;
  private sessionStartTime: number | null = null;
  private currentSetId: string | undefined = undefined;
  private eventQueue: LearningEvent[] = [];
  private flushInterval: NodeJS.Timeout | null = null;
  private isEnabled: boolean = true;

  private constructor() {
    // 每30秒自动刷新事件队列
    this.flushInterval = setInterval(() => {
      this.flushEventQueue();
    }, 30000);

    // 页面卸载前发送剩余事件
    window.addEventListener('beforeunload', () => {
      this.flushEventQueue();
    });

    // 页面隐藏时发送事件
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.endStudySession();
      } else {
        this.startStudySession();
      }
    });
  }

  public static getInstance(): LearningEventTracker {
    if (!LearningEventTracker.instance) {
      LearningEventTracker.instance = new LearningEventTracker();
    }
    return LearningEventTracker.instance;
  }

  // 开始学习会话
  public startStudySession(setId?: string): void {
    if (!this.isEnabled) return;

    this.sessionStartTime = Math.floor(Date.now() / 1000);
    this.currentSetId = setId;

    console.log('[LearningEventTracker] 学习会话开始', {
      startTime: this.sessionStartTime,
      setId: this.currentSetId
    });
  }

  // 结束学习会话
  public endStudySession(): void {
    if (!this.isEnabled || !this.sessionStartTime) return;

    const now = Math.floor(Date.now() / 1000);
    const sessionDuration = now - this.sessionStartTime;

    if (sessionDuration >= 30) { // 只记录超过30秒的会话
      this.recordEvent({
        event_type: 'study_session',
        study_time_seconds: sessionDuration,
        set_id: this.currentSetId,
        event_data: {
          session_start_time: this.sessionStartTime,
          session_end_time: now,
          duration_seconds: sessionDuration
        }
      });
    }

    this.sessionStartTime = null;
    console.log('[LearningEventTracker] 学习会话结束', { duration: sessionDuration });
  }

  // 记录卡片完成事件
  public recordCardCompleted(setId: string, count: number = 1): void {
    if (!this.isEnabled) return;

    this.recordEvent({
      event_type: 'card_completed',
      cards_count: count,
      set_id: setId,
      event_data: {
        completion_timestamp: Math.floor(Date.now() / 1000)
      }
    });

    console.log('[LearningEventTracker] 卡片完成', { setId, count });
  }

  // 记录测验完成事件
  public recordQuizCompleted(setId: string, accuracy: number, totalCards: number): void {
    if (!this.isEnabled) return;

    this.recordEvent({
      event_type: 'quiz_completed',
      accuracy_rate: accuracy,
      cards_count: totalCards,
      set_id: setId,
      event_data: {
        quiz_timestamp: Math.floor(Date.now() / 1000),
        total_cards: totalCards,
        correct_answers: Math.round(totalCards * accuracy / 100)
      }
    });

    console.log('[LearningEventTracker] 测验完成', { setId, accuracy, totalCards });
  }

  // 记录阅读完成事件
  public recordPassageRead(passageId: string, readingTime?: number): void {
    if (!this.isEnabled) return;

    this.recordEvent({
      event_type: 'passage_read',
      passage_id: passageId,
      study_time_seconds: readingTime,
      event_data: {
        reading_timestamp: Math.floor(Date.now() / 1000)
      }
    });

    console.log('[LearningEventTracker] 阅读完成', { passageId, readingTime });
  }

  // 记录自定义事件
  public recordCustomEvent(event: LearningEvent): void {
    if (!this.isEnabled) return;

    this.recordEvent(event);
    console.log('[LearningEventTracker] 自定义事件', event);
  }

  // 添加事件到队列
  private recordEvent(event: LearningEvent): void {
    this.eventQueue.push(event);

    // 如果队列中有5个或更多事件，立即发送
    if (this.eventQueue.length >= 5) {
      this.flushEventQueue();
    }
  }

  // 刷新事件队列
  private async flushEventQueue(): Promise<void> {
    if (this.eventQueue.length === 0) return;

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    try {
      // 批量发送事件
      for (const event of eventsToSend) {
        await progressApi.recordLearningEvent(event);
      }

      console.log(`[LearningEventTracker] 成功发送 ${eventsToSend.length} 个学习事件`);
    } catch (error) {
      console.error('[LearningEventTracker] 发送学习事件失败:', error);

      // 发送失败，将事件重新加入队列（但限制队列大小）
      const maxQueueSize = 20;
      if (this.eventQueue.length < maxQueueSize) {
        this.eventQueue.unshift(...eventsToSend.slice(-maxQueueSize + this.eventQueue.length));
      }
    }
  }

  // 启用/禁用追踪器
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    console.log('[LearningEventTracker] 追踪器', enabled ? '已启用' : '已禁用');
  }

  // 检查是否启用了追踪
  public isTrackingEnabled(): boolean {
    return this.isEnabled;
  }

  // 获取当前会话信息
  public getCurrentSessionInfo(): { startTime: number | null; duration: number | null } | null {
    if (!this.sessionStartTime) return null;

    const now = Math.floor(Date.now() / 1000);
    return {
      startTime: this.sessionStartTime,
      duration: now - this.sessionStartTime
    };
  }

  // 手动刷新队列
  public async flushNow(): Promise<void> {
    await this.flushEventQueue();
  }

  // 销毁追踪器
  public destroy(): void {
    this.endStudySession();
    this.flushEventQueue();

    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }

    this.isEnabled = false;
  }
}

// 导出单例实例
export const learningTracker = LearningEventTracker.getInstance();

// 导出便捷函数
export const startStudySession = (setId?: string) => learningTracker.startStudySession(setId);
export const endStudySession = () => learningTracker.endStudySession();
export const recordCardCompleted = (setId: string, count?: number) => learningTracker.recordCardCompleted(setId, count);
export const recordQuizCompleted = (setId: string, accuracy: number, totalCards: number) => learningTracker.recordQuizCompleted(setId, accuracy, totalCards);
export const recordPassageRead = (passageId: string, readingTime?: number) => learningTracker.recordPassageRead(passageId, readingTime);
export const recordCustomEvent = (event: LearningEvent) => learningTracker.recordCustomEvent(event);